import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import {
  Box,
  Paper,
  Typography,
  Button,
  Chip,
  Grid,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Alert,
} from '@mui/material'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddIcon from '@mui/icons-material/Add'
import VisibilityIcon from '@mui/icons-material/Visibility'
import AssignmentIcon from '@mui/icons-material/Assignment'
import { provisioningApi } from '../services/provisioningApi'
import { getInstanceProjects, InstanceConfig, normalizeInstanceConfig } from '../utils/instanceApi'
import { Project, ProjectStatus } from '@shared/api-client'

const statusColors: Record<ProjectStatus, 'default' | 'primary' | 'warning' | 'success' | 'error'> = {
  planning: 'default',
  active: 'primary',
  on_hold: 'warning',
  completed: 'success',
  cancelled: 'error',
}

const priorityColors: Record<string, 'default' | 'primary' | 'warning' | 'error'> = {
  low: 'default',
  medium: 'primary',
  high: 'warning',
  urgent: 'error',
}

export default function OrganizationDetails() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const [organization, setOrganization] = useState<InstanceConfig | null>(null)
  const [projects, setProjects] = useState<Project[]>([])
  const [loading, setLoading] = useState(true)
  const [projectsLoading, setProjectsLoading] = useState(true)
  const [projectsError, setProjectsError] = useState<string | null>(null)

  useEffect(() => {
    if (id) {
      loadOrganization()
    }
  }, [id])

  const loadOrganization = async () => {
    try {
      const data = await provisioningApi.getInstance(id!)
      setOrganization(normalizeInstanceConfig(data))
    } catch (error: any) {
      console.error('Failed to load organization:', error)
      alert(error.response?.data?.detail || 'Failed to load organization')
    } finally {
      setLoading(false)
    }
  }

  const loadProjects = async () => {
    if (!organization) return
    
    // Don't try to load projects if instance is not active
    if (organization.status !== 'active') {
      setProjectsLoading(false)
      setProjectsError(null)
      return
    }
    
    try {
      setProjectsLoading(true)
      setProjectsError(null)
      const data = await getInstanceProjects(organization)
      setProjects(data)
    } catch (error: any) {
      // Handle different error types - show in UI instead of alerts
      const status = error.response?.status
      const code = error.code
      const message = error.message || ''
      
      // Connection errors (instance not ready)
      if (
        status === 503 || 
        status === 502 || 
        status === 504 ||
        code === 'ECONNREFUSED' || 
        code === 'ERR_NETWORK' ||
        code === 'ETIMEDOUT' ||
        message.includes('Network Error') ||
        message.includes('timeout') ||
        message.includes('refused')
      ) {
        // Instance is not ready yet - this is expected for newly provisioned instances
        setProjectsError('Instance is still initializing. Projects will be available once the instance is ready.')
        console.info(`Instance ${organization.id} not ready yet (${status || code}). Projects will load when instance is active.`)
      } 
      // Authentication errors (401) - token not valid for this instance
      else if (status === 401) {
        setProjectsError('Authentication failed. The admin token may not be valid for this instance.')
        console.warn(`Authentication failed for instance ${organization.id} - admin token may not be valid for this instance`)
      }
      // Other errors
      else {
        const errorMsg = error.response?.data?.detail || error.message || 'Failed to load projects'
        setProjectsError(errorMsg)
        console.warn(`Error loading projects for instance ${organization.id}:`, {
          status,
          code,
          message: error.message,
          response: error.response?.data
        })
      }
      // Always set projects to empty array on error
      setProjects([])
    } finally {
      setProjectsLoading(false)
    }
  }

  useEffect(() => {
    if (organization) {
      loadProjects()
    }
  }, [organization])

  const formatDate = (date: string | null) => {
    if (!date) return '-'
    return new Date(date).toLocaleDateString()
  }

  if (loading) {
    return <Typography>Loading...</Typography>
  }

  if (!organization) {
    return <Typography>Organization not found</Typography>
  }

  const stats = {
    total: projects.length,
    active: projects.filter(p => p.status === 'active').length,
    completed: projects.filter(p => p.status === 'completed').length,
    onHold: projects.filter(p => p.status === 'on_hold').length,
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/organizations')}
        >
          Back to Organizations
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => navigate(`/organizations/${id}/projects/add`)}
          disabled={organization.status !== 'active'}
        >
          Add Project
        </Button>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
          <Typography variant="h4">{organization.name}</Typography>
          <Chip
            label={organization.status}
            color={organization.status === 'active' ? 'success' : 'warning'}
          />
        </Box>

        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Organization Information
                </Typography>
                <Typography><strong>Domain:</strong> {organization.organization_domain}</Typography>
                <Typography><strong>Admin Email:</strong> {organization.admin_email}</Typography>
                <Typography><strong>API URL:</strong> <code>{organization.api_url}</code></Typography>
                <Typography><strong>Created:</strong> {formatDate(organization.created_at)}</Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} md={6}>
            <Card variant="outlined">
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Project Statistics
                </Typography>
                <Typography><strong>Total Projects:</strong> {stats.total}</Typography>
                <Typography><strong>Active:</strong> {stats.active}</Typography>
                <Typography><strong>Completed:</strong> {stats.completed}</Typography>
                <Typography><strong>On Hold:</strong> {stats.onHold}</Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3 }}>
        <Typography variant="h5" gutterBottom>
          Projects ({projects.length})
        </Typography>
        {organization.status !== 'active' ? (
          <Alert severity="warning" sx={{ mt: 2 }}>
            This organization instance is currently <strong>{organization.status}</strong>. 
            Projects will be available once the instance is active and ready.
          </Alert>
        ) : projectsError ? (
          <Alert severity="info" sx={{ mt: 2 }}>
            {projectsError}
          </Alert>
        ) : projectsLoading ? (
          <Typography sx={{ mt: 2 }}>Loading projects...</Typography>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>ID</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Priority</TableCell>
                  <TableCell>Progress</TableCell>
                  <TableCell>Start Date</TableCell>
                  <TableCell>End Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {projects.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No projects found. The instance may still be initializing.
                    </TableCell>
                  </TableRow>
                ) : (
                  projects.map((project) => (
                    <TableRow key={project.id} hover>
                      <TableCell>{project.id}</TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <AssignmentIcon fontSize="small" />
                          {project.name}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={project.status}
                          color={statusColors[project.status]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={project.priority}
                          color={priorityColors[project.priority]}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 60,
                              height: 8,
                              bgcolor: 'grey.300',
                              borderRadius: 1,
                              overflow: 'hidden',
                            }}
                          >
                            <Box
                              sx={{
                                width: `${project.progress}%`,
                                height: '100%',
                                bgcolor: 'primary.main',
                              }}
                            />
                          </Box>
                          <Typography variant="body2">{project.progress}%</Typography>
                        </Box>
                      </TableCell>
                      <TableCell>{formatDate(project.start_date)}</TableCell>
                      <TableCell>{formatDate(project.end_date)}</TableCell>
                      <TableCell align="right">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => navigate(`/organizations/${id}/projects/${project.id}`)}
                        >
                          <VisibilityIcon />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
    </Box>
  )
}

